import { z } from 'zod';
import { IdSchema, IsoDateTimeSchema, OrgIdSchema, UserIdSchema } from './common.js';
/**
 * A scanner run against an approved scope.
 *
 * `PASSIVE` observes traffic from a crawl and never sends an attack payload. `ACTIVE` does,
 * which is why it sits behind its own approval gate and its own scope permit.
 */
export const ScanKindSchema = z.enum(['PASSIVE', 'ACTIVE']);
export const ScanStatusSchema = z.enum([
    'QUEUED',
    'RUNNING',
    'SUCCEEDED',
    'FAILED',
    /** Stopped because it left its window or exceeded its duration, not because it errored. */
    'STOPPED',
]);
export const ScanSchema = z.object({
    id: IdSchema,
    orgId: OrgIdSchema,
    engagementId: IdSchema,
    /** Denormalised from the engagement so ingest can key findings without a second read. */
    applicationId: IdSchema,
    kind: ScanKindSchema,
    status: ScanStatusSchema,
    /** The scope version this scan was authorised against — never "the current one". */
    scopeVersion: z.number().int().positive(),
    requestedByUserId: UserIdSchema,
    requestedAt: IsoDateTimeSchema,
    startedAt: IsoDateTimeSchema.optional(),
    finishedAt: IsoDateTimeSchema.optional(),
    /** ECS task ARN, for operators correlating a scan with its container. */
    taskArn: z.string().optional(),
    findingCount: z.number().int().nonnegative().optional(),
    /**
     * The per-scan egress groups, recorded on the row rather than read back from the task event.
     *
     * An ECS task state change carries the ENI's subnet, interface id, MAC and private address —
     * and not its security groups. Ingest looked for them there, found nothing every time, and
     * deleted nothing, so a group accumulated per scan against the VPC limit. Whoever creates a
     * group writes its id here, because the deleter has to be told.
     */
    egressSecurityGroupIds: z.array(z.string()).optional(),
    /**
     * When the platform stops waiting.
     *
     * The duration budget from the approved scope, plus a margin for the task to stop and its
     * event to arrive. Past this a scan is overdue and the sweep reconciles it, so a missed
     * event costs a delay rather than a row that reads RUNNING for ever.
     */
    deadlineAt: IsoDateTimeSchema.optional(),
    /** A message, never scanner output — target content is untrusted (§4). */
    error: z.string().nullish(),
});
/** POST /v1/engagements/{id}/scans */
export const CreateScanRequestSchema = z.strictObject({
    kind: ScanKindSchema,
    /** The scope version the requester believes is approved; checked against the engagement. */
    scopeVersion: z.number().int().positive(),
});
export const ListScansResponseSchema = z.object({ items: z.array(ScanSchema) });
//# sourceMappingURL=scan.js.map